import { useState } from 'react'

import { IllustrationName } from '@/components/icons/Illustration'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Dropzone } from '@/components/ui/dropzone'
import { MultipleDropzone } from '@/components/ui/multiple-dropzone'
import { cn } from '../../lib/utils'
import { ChatActionWithValue } from '../../models/insuranceApplication'
import { IllustratedRow } from './ConfirmInfoCard'

export interface ChatFileUploadProps {
  accept: string
  handleSubmit: (data: ChatActionWithValue | FormData) => void
  multiple: boolean
  label: string
  readonly: boolean
  /** Loss run requests show the loss run artwork; plain document uploads show the label alone. */
  illustration?: IllustrationName
}

export function ChatFileUpload({
  accept,
  handleSubmit,
  multiple,
  label,
  readonly,
  illustration,
}: ChatFileUploadProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [files, setFiles] = useState<File[] | null>(null)

  function onSubmit() {
    if (!files?.length) {
      return
    }

    const formData = new FormData()
    for (const file of files) {
      formData.append('file', file)
    }
    formData.append('content', `Uploaded ${files.length} file${files.length > 1 ? 's' : ''}`)

    handleSubmit(formData)
    // The upload toast reports progress from here; an open dialog read as nothing having happened.
    setIsOpen(false)
  }

  return (
    <>
      <div
        className={cn(
          'mt-6 flex w-full cursor-pointer items-center justify-end gap-2 font-semibold',
          readonly && 'cursor-auto opacity-50'
        )}
        onClick={readonly ? undefined : () => setIsOpen(true)}
      >
        <Card className="w-full">
          <CardContent>
            {illustration ? (
              <IllustratedRow
                illustration={illustration}
                label={label}
                action={
                  !readonly && (
                    <Button variant="outlineThin" size="sm">
                      Upload
                    </Button>
                  )
                }
              />
            ) : (
              <div className="flex w-full items-center gap-2">
                <div>{label}</div>
                {!readonly && (
                  <Button variant="outlineThin" size="sm" className="ml-auto">
                    Upload
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={isOpen} onOpenChange={() => setIsOpen(false)}>
        <DialogContent size="lg">
          <div className="text-center text-lg font-semibold">{label}</div>
          {multiple ? (
            <MultipleDropzone onChange={setFiles} accept={accept} />
          ) : (
            <Dropzone onChange={(file) => file && setFiles([file])} accept={accept} />
          )}
          <div className="mt-5 flex justify-end gap-2">
            <Button onClick={() => setIsOpen(false)} variant="ghost">
              Cancel
            </Button>
            <Button onClick={onSubmit} disabled={!files?.length}>
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
